import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { semanticJurisdiction } from '@/lib/helpers';
import { queryClient } from '@/lib/query';
import type { TPlainObject } from '@/lib/types';

export const jurisdictionTitle = (code: string | null | undefined) =>
  code ? semanticJurisdiction(code) : '-';

export const CLIENT_COMPANY_STATUSES = [
  'in_registration',
  'registration_failed',
  'in_service',
  'liquidated',
  'lost_client',
  'not_registered_by_us',
] as const;

export type ClientCompanyStatus = (typeof CLIENT_COMPANY_STATUSES)[number];

export const CLIENT_COMPANY_STATUS_LABELS: Record<ClientCompanyStatus, string> =
  {
    in_registration: 'In registration',
    registration_failed: 'Registration failed',
    in_service: 'In service',
    liquidated: 'Liquidated',
    lost_client: 'Lost client',
    not_registered_by_us: 'Not registered by us',
  };

export const CLIENT_COMPANY_STATUS_COLORS: Record<ClientCompanyStatus, string> =
  {
    in_registration: 'blue',
    registration_failed: 'red',
    in_service: 'green',
    liquidated: 'gray',
    lost_client: 'orange',
    not_registered_by_us: 'grape',
  };

export type ClientCompanyListRow = {
  id: number;
  name: string;
  legalName: string | null;
  registrationNumber: string | null;
  status: ClientCompanyStatus;
  jurisdiction: string | null;
  registrationDate: string | null;
  mondayItemId: number | string | null;
  isTest: boolean;
  accountId: number;
  accountName: string | null;
  contactId: number | null;
  contactName: string | null;
  services: number;
  unmapped: number;
  nextRenewalDate: string | null;
};

export type PortfolioContactRow = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  mondayItemId: number | string | null;
  accountId: number;
  accountName: string | null;
  services: number;
  unmapped: number;
  pending: number;
};

export type ListResponse<T> = { items: T[]; total: number };

export type ClientCompanySearchFilters = {
  q?: string;
  status?: ClientCompanyStatus;
  unmapped?: boolean;
  limit: number;
  offset: number;
};

export type PortfolioContactFilters = {
  q?: string;
  limit: number;
  offset: number;
};

export const clientCompanyKeys = {
  all: ['client-companies'] as const,
  search: (f: ClientCompanySearchFilters) =>
    [...clientCompanyKeys.all, 'search', f] as const,
  portfolioContacts: (f: PortfolioContactFilters) =>
    [...clientCompanyKeys.all, 'portfolio-contacts', f] as const,
  detail: (id: number) => [...clientCompanyKeys.all, 'detail', id] as const,
  contactPortfolio: (contactId: number) =>
    [...clientCompanyKeys.all, 'contact-portfolio', contactId] as const,
};

const callGet = <T>(endpoint: string, json?: TPlainObject) =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

export function useClientCompaniesSearch(
  filters: ClientCompanySearchFilters,
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: clientCompanyKeys.search(filters),
    queryFn: () =>
      callGet<ListResponse<ClientCompanyListRow>>('client-companies/search', {
        q: filters.q || undefined,
        status: filters.status,
        unmapped: filters.unmapped || undefined,
        limit: filters.limit,
        offset: filters.offset,
      }),
    placeholderData: keepPreviousData,
  });
}

export function usePortfolioContacts(
  filters: PortfolioContactFilters,
  enabled = true,
) {
  return useQuery({
    enabled,
    queryKey: clientCompanyKeys.portfolioContacts(filters),
    queryFn: () =>
      callGet<ListResponse<PortfolioContactRow>>(
        'client-companies/portfolio-contacts',
        {
          q: filters.q || undefined,
          limit: filters.limit,
          offset: filters.offset,
        },
      ),
    placeholderData: keepPreviousData,
  });
}

export type PartyRole = 'director' | 'ubo' | 'shareholder';

export const REQUIRED_FIELD_KEYS = [
  'legalName',
  'registrationNumber',
  'registrationDate',
  'jurisdiction',
  'address',
  'email',
  'registryUrl',
] as const;

export type RequiredFieldKey = (typeof REQUIRED_FIELD_KEYS)[number];

export const DOCUMENT_REQUIREMENT_LABELS: Record<string, string> = {
  registry_extract: 'Registry extract',
  articles: 'Articles of association',
  proof_of_address: 'Proof of address',
  aml: 'AML documents',
  shareholder_structure: 'Shareholder structure',
  director_document: 'Director document',
  other: 'Other',
};

export const COMPANY_FIELD_LABELS: Record<string, string> = {
  name: 'Working name',
  legalName: 'Full company name',
  registrationNumber: 'Registration number',
  registrationDate: 'Registration date',
  transferDate: 'Transfer date',
  jurisdiction: 'Jurisdiction',
  postalCode: 'Postal code',
  domain: 'Domain',
  address: 'Address',
  email: 'Company email',
  registryUrl: 'Registry link',
  contactId: 'Main contact',
};

export const PARTY_ROLE_LABELS: Record<PartyRole, string> = {
  director: 'Director',
  shareholder: 'Shareholder',
  ubo: 'UBO',
};

export const requirementLabel = (key: string) =>
  COMPANY_FIELD_LABELS[key] ??
  DOCUMENT_REQUIREMENT_LABELS[key] ??
  PARTY_ROLE_LABELS[key as PartyRole] ??
  key;

export type ClientCompany = {
  id: number;
  kind: 'client' | 'external';
  status: ClientCompanyStatus | null;
  accountId: number | null;
  accountName: string | null;
  contactId: number | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactEmail: string | null;
  sourceOfferId: number | null;
  name: string;
  legalName: string | null;
  registrationNumber: string | null;
  registrationDate: string | null;
  transferDate: string | null;
  jurisdiction: string | null;
  postalCode: string | null;
  domain: string | null;
  address: string | null;
  email: string | null;
  registryUrl: string | null;
  mondayItemId: number | string | null;
  mondayGroup: string | null;
  mondayExtra: Record<string, unknown>;
  isTest: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CompanyRequirements = {
  fields: { key: RequiredFieldKey; filled: boolean }[];
  people: { role: 'director' | 'shareholder'; filled: boolean }[];
  documents: { requirement: string; filled: boolean }[];
  missing: string[];
  complete: boolean;
};

export type CompanyParty = {
  id: number;
  role: PartyRole;
  contactId: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  partyCompanyId: number | null;
  partyCompanyName: string | null;
  partyCompanyKind: 'client' | 'external' | null;
  sharePercent: string | number | null;
  createdAt: string;
};

export type CompanyDocument = {
  id: number;
  filename: string | null;
  name: string | null;
  type: string | null;
  size: number | null;
  createdAt: string;
  requirements: string[];
  fromMonday: boolean;
};

export type ServiceCycle = {
  id: number;
  periodStart: string | null;
  renewalDate: string | null;
  periodEnd: string | null;
  title: string | null;
  price: string | number | null;
  units: string | number | null;
  currency: string | null;
  mondayStatus: string | null;
  mondayItemId: number | string | null;
  mondayDealItemId: number | string | null;
  source: 'monday' | 'delivery' | 'manual';
  startSetBy: number | null;
  startSetByName: string | null;
  startSetAt: string | null;
};

export type PortfolioService = {
  id: number;
  origin: 'offer' | 'monday_company' | 'monday_deal';
  kind: 'recurring' | 'one_off';
  parentId: number | null;
  title: string;
  description: string | null;
  serviceId: number | null;
  catalogName: string | null;
  mappingStatus: 'mapped' | 'unmapped' | 'not_required';
  offerId: number | null;
  offerTitle: string | null;
  offerItemId: number | null;
  blockUuid: string | null;
  renewalPeriod: string | null;
  renewalPrice: string | number | null;
  renewalCurrency: string | null;
  price: string | number | null;
  units: string | number | null;
  currency: string | null;
  mondayProductItemId: number | string | null;
  mondayStatus: Record<string, unknown> | null;
  deliveryState: 'pending' | 'delivered';
  deliveredAt: string | null;
  deliveredBy: number | null;
  deliveredByName: string | null;
  deliveredSource: 'bo' | 'monday' | null;
  mondayItemId: number | string | null;
  mondayBoardId: number | string | null;
  mondayParentItemId: number | string | null;
  notes: string | null;
  createdAt: string;
  cycles: ServiceCycle[];
  currentCycle: ServiceCycle | null;
  nextRenewalDate: string | null;
  paid?: boolean | null;
  paidState?: 'paid' | 'not_settled' | 'not_assigned' | null;
};

export const NOT_PAID_REASONS: Record<string, string> = {
  not_settled: 'Not paid: the installment covering this service is not Settled',
  not_assigned: 'Not paid: the service is not assigned to any installment',
};

export type ServiceEvent = {
  id: number;
  kind: string;
  payload: Record<string, unknown>;
  actorUserId: number | null;
  actorName: string | null;
  actorEmail: string | null;
  createdAt: string;
};

export type CompanyEvent = {
  id: number;
  kind: string;
  payload: Record<string, unknown>;
  actorUserId: number | null;
  actorName: string | null;
  actorEmail: string | null;
  createdAt: string;
};

export type ClientCompanyCard = {
  readOnly?: boolean;
  company: ClientCompany;
  allowedStatuses: ClientCompanyStatus[];
  requirements: CompanyRequirements;
  parties: CompanyParty[];
  documents: CompanyDocument[];
  services: PortfolioService[];
  events: CompanyEvent[];
};

export type PartyCandidateContact = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  accountId: number;
  accountName: string | null;
  sameAccount: boolean;
  visible: boolean;
};

export type PartyCandidateCompany = {
  id: number;
  kind: 'client' | 'external';
  name: string;
  legalName: string | null;
  jurisdiction: string | null;
  registrationNumber: string | null;
  status: ClientCompanyStatus | null;
};

export type PartyCandidates = {
  contacts: PartyCandidateContact[];
  companies: PartyCandidateCompany[];
};

export type CompanyUpdate = Partial<
  Pick<
    ClientCompany,
    | 'name'
    | 'legalName'
    | 'registrationNumber'
    | 'registrationDate'
    | 'transferDate'
    | 'jurisdiction'
    | 'postalCode'
    | 'domain'
    | 'address'
    | 'email'
    | 'registryUrl'
    | 'contactId'
  >
>;

export type PartyLink = {
  companyId: number;
  role: PartyRole;
  contactId?: number;
  newContact?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  partyCompanyId?: number;
  newExternal?: {
    name: string;
    jurisdiction?: string;
    registrationNumber?: string;
  };
  sharePercent?: number | null;
};

export function apiErrorText(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const parsed = JSON.parse(raw);
    return parsed.message ?? parsed.error ?? raw;
  } catch {
    return raw;
  }
}

const handled = <T>(p: Promise<T>): Promise<T> =>
  p.catch((e) => {
    if (e && typeof e === 'object') Object.assign(e, { handled: true });
    throw e;
  });

const callPost = <T>(endpoint: string, json: object) =>
  handled(
    call<T>(endpoint, { json: json as TPlainObject }).then((r) => r.json()),
  );

const callForm = <T>(endpoint: string, body: FormData) =>
  handled(call<T>(endpoint, { body }).then((r) => r.json()));

export function useClientCompany(id: number | null | undefined) {
  return useQuery({
    enabled: id != null && id > 0,
    queryKey: clientCompanyKeys.detail(id ?? 0),
    queryFn: () =>
      callGet<ClientCompanyCard>('client-companies/details', { id }).then(
        (card) =>
          card.readOnly
            ? {
                ...card,
                requirements: card.requirements ?? {
                  fields: [],
                  people: [],
                  documents: [],
                  missing: [],
                  complete: true,
                },
                documents: card.documents ?? [],
                events: card.events ?? [],
              }
            : card,
      ),
  });
}

const setCard = (card: ClientCompanyCard) => {
  queryClient.setQueryData(
    clientCompanyKeys.detail(Number(card.company.id)),
    card,
  );
  queryClient.invalidateQueries({
    queryKey: [...clientCompanyKeys.all, 'search'],
  });
};

export function useUpdateClientCompany() {
  return useMutation({
    mutationFn: (body: CompanyUpdate & { id: number }) =>
      callPost<ClientCompanyCard>('client-companies/update', body),
    onSuccess: setCard,
  });
}

export function useChangeClientCompanyStatus() {
  return useMutation({
    mutationFn: (body: { id: number; status: ClientCompanyStatus }) =>
      callPost<ClientCompanyCard>('client-companies/status', body),
    onSuccess: setCard,
  });
}

export function useLinkParty() {
  return useMutation({
    mutationFn: (body: PartyLink) =>
      callPost<ClientCompanyCard>('client-companies/parties-link', body),
    onSuccess: setCard,
  });
}

export function useUnlinkParty() {
  return useMutation({
    mutationFn: (body: { id: number }) =>
      callPost<ClientCompanyCard>('client-companies/parties-unlink', body),
    onSuccess: setCard,
  });
}

export function usePartyCandidates(
  companyId: number,
  q: string,
  enabled: boolean,
) {
  return useQuery({
    enabled,
    queryKey: [...clientCompanyKeys.all, 'party-candidates', companyId, q],
    queryFn: () =>
      callGet<PartyCandidates>('client-companies/party-candidates', {
        companyId,
        q,
      }),
    placeholderData: keepPreviousData,
  });
}

export type ServiceComment = {
  id: number;
  body: string;
  authorUserId: number;
  authorName: string | null;
  authorEmail: string | null;
  createdAt: string;
};

export type ServiceFile = {
  id: number;
  filename: string | null;
  name: string | null;
  type: string | null;
  size: number | null;
  createdAt: string;
  linkedBy: number | null;
  linkedByName: string | null;
  linkedAt: string;
};

export type ContactPortfolio = {
  readOnly?: boolean;
  contact: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    mondayItemId: number | string | null;
    accountId: number;
    accountName: string | null;
  };
  services: PortfolioService[];
  companies: { id: number; name: string; status: ClientCompanyStatus }[];
};

export const serviceKeys = {
  all: ['client-services'] as const,
  events: (id: number) => ['client-services', 'events', id] as const,
  comments: (id: number) => ['client-services', 'comments', id] as const,
  files: (id: number) => ['client-services', 'files', id] as const,
};

export function useContactPortfolio(contactId: number) {
  return useQuery({
    enabled: contactId > 0,
    queryKey: clientCompanyKeys.contactPortfolio(contactId),
    queryFn: () =>
      callGet<ContactPortfolio>('client-companies/contact-portfolio', {
        contactId,
      }),
    retry: false,
  });
}

export function useServiceComments(serviceId: number, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: serviceKeys.comments(serviceId),
    queryFn: () =>
      callGet<ServiceComment[]>('client-services/comments', { serviceId }),
  });
}

export function useServiceFiles(serviceId: number, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: serviceKeys.files(serviceId),
    queryFn: () =>
      callGet<ServiceFile[]>('client-services/files', { serviceId }),
  });
}

export function useAddServiceComment(serviceId: number) {
  return useMutation({
    mutationFn: (body: string) =>
      callPost<ServiceComment>('client-services/comment', { serviceId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceKeys.comments(serviceId),
      });
      queryClient.invalidateQueries({
        queryKey: serviceKeys.events(serviceId),
      });
    },
  });
}

export function useUploadServiceFiles(serviceId: number) {
  return useMutation({
    mutationFn: async (files: File[]) => {
      const body = new FormData();
      body.append('serviceId', String(serviceId));
      for (const f of files) body.append('file', f, f.name);
      return callForm<ServiceFile[]>('client-services/files-upload', body);
    },
    onSuccess: (files) => {
      queryClient.setQueryData(serviceKeys.files(serviceId), files);
      queryClient.invalidateQueries({
        queryKey: serviceKeys.events(serviceId),
      });
    },
  });
}

export async function downloadFile(
  endpoint: string,
  params: TPlainObject,
  filename: string,
) {
  const res = await call(endpoint, { method: 'GET', json: params });
  const href = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export const DOCUMENT_REQUIREMENTS = [
  'registry_extract',
  'articles',
  'shareholder_structure',
  'director_document',
  'proof_of_address',
  'aml',
  'other',
] as const;

export type DocumentRequirement = (typeof DOCUMENT_REQUIREMENTS)[number];

export function useUploadCompanyDocuments(companyId: number) {
  return useMutation({
    mutationFn: async (v: {
      requirement: DocumentRequirement;
      files: File[];
    }) => {
      const body = new FormData();
      body.append('companyId', String(companyId));
      body.append('requirement', v.requirement);
      for (const f of v.files) body.append('file', f, f.name);
      return callForm<ClientCompanyCard>(
        'client-companies/documents-upload',
        body,
      );
    },
    onSuccess: setCard,
  });
}

export function useAssignCompanyDocument() {
  return useMutation({
    mutationFn: (body: {
      companyId: number;
      documentId: number;
      requirement: DocumentRequirement;
    }) =>
      callPost<ClientCompanyCard>('client-companies/documents-assign', body),
    onSuccess: setCard,
  });
}

export type MondayLink =
  | { kind: 'client_company'; id: number }
  | { kind: 'contact'; id: number; view: 'contact' | 'portfolio' }
  | { kind: 'lead'; id: number }
  | { kind: 'account'; id: number }
  | {
      kind: 'deal';
      owners: { kind: 'client_company' | 'contact'; id: number }[];
    };

export const mondayLinkPath = (link: {
  kind: string;
  id?: number;
  view?: string;
}): string | null => {
  switch (link.kind) {
    case 'client_company':
      return `/client-companies/${link.id}`;
    case 'contact':
      return link.view === 'contact'
        ? `/contacts/${link.id}`
        : `/client-contacts/${link.id}`;
    case 'lead':
      return `/leads/${link.id}`;
    case 'account':
      return `/accounts/${link.id}`;
    default:
      return null;
  }
};

export function useResolveMonday(itemId: string | number, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['monday-links', 'resolve', String(itemId)],
    queryFn: () => callGet<MondayLink>('monday-links/resolve', { itemId }),
    retry: false,
  });
}

const invalidatePortfolios = () => {
  queryClient.invalidateQueries({ queryKey: clientCompanyKeys.all });
  queryClient.invalidateQueries({ queryKey: serviceKeys.all });
};

export function useServiceEvents(serviceId: number, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: serviceKeys.events(serviceId),
    queryFn: () =>
      callGet<ServiceEvent[]>('client-services/events', { serviceId }),
  });
}

export function useDeliverService() {
  return useMutation({
    mutationFn: async (v: {
      serviceId: number;
      periodStart?: string | null;
      comment?: string;
      files?: File[];
    }) => {
      const body = new FormData();
      body.append('serviceId', String(v.serviceId));
      if (v.periodStart) body.append('periodStart', v.periodStart);
      if (v.comment?.trim()) body.append('comment', v.comment.trim());
      for (const f of v.files ?? []) body.append('file', f, f.name);
      return callForm<{
        serviceId: number;
        cycle: { id: number; periodStart: string; renewalDate: string } | null;
      }>('client-services/deliver', body);
    },
    onSettled: (_d, _e, v) => {
      invalidatePortfolios();
      queryClient.invalidateQueries({
        queryKey: serviceKeys.comments(v.serviceId),
      });
      queryClient.invalidateQueries({
        queryKey: serviceKeys.files(v.serviceId),
      });
    },
  });
}

export function useUpdateCycle() {
  return useMutation({
    mutationFn: (body: {
      serviceId: number;
      periodStart?: string;
      renewalDate?: string;
    }) =>
      callPost<{ cycleId: number; changed: string[] }>(
        'client-services/cycle-update',
        body,
      ),
    onSuccess: invalidatePortfolios,
  });
}

export const RENEWAL_MONTHS: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  biannually: 6,
  annually: 12,
  biennial: 24,
};

export function deliveryErrorText(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const p = JSON.parse(raw);
    if (p.code === 'NOT_PAID')
      return 'Not paid: the proforma covering this service is not Settled yet.';
    if (p.code === 'ALREADY_DELIVERED') return 'Already marked delivered.';
    if (p.code === 'REQUIREMENTS' && Array.isArray(p.missing))
      return `Company data missing: ${p.missing.map(requirementLabel).join(', ')}`;
    return p.message ?? p.error ?? raw;
  } catch {
    return raw;
  }
}

export type OfferClientCompanyCandidates = {
  clientCompanyId: number | string | null;
  companies: {
    id: number | string;
    name: string;
    legalName: string | null;
    registrationNumber: string | null;
    jurisdiction: string | null;
    status: ClientCompanyStatus;
  }[];
};

export function useOfferClientCompanyCandidates(offerId: number) {
  return useQuery({
    queryKey: ['offers', 'client-company-candidates', offerId],
    queryFn: () =>
      callGet<OfferClientCompanyCandidates>('offer/client-company-candidates', {
        offerId,
      }),
  });
}

export function useSetOfferClientCompany(offerId: number) {
  return useMutation({
    mutationFn: (clientCompanyId: number | null) =>
      callPost<{ offerId: number; clientCompanyId: number | null }>(
        'offer/client-company',
        { offerId, clientCompanyId },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['offers', 'client-company-candidates', offerId],
      });
      queryClient.invalidateQueries({
        queryKey: ['offers', 'detail', offerId],
      });
    },
  });
}
